import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import * as ZXing from 'html5-qrcode/third_party/zxing-js.umd';

// Helper to load file as HTMLImageElement
const loadImage = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

// Advanced Multi-Scale & Pre-Processing Barcode Scanner using TRY_HARDER on ZXing
const scanImageForBarcodes = async (img) => {
    const zx = ZXing.default || ZXing;
    const uniqueCodes = new Set();

    // 1. Try native Web BarcodeDetector API if available (extremely fast, robust, and supports multiple codes)
    if ('BarcodeDetector' in window) {
        try {
            const formats = await window.BarcodeDetector.getSupportedFormats();
            if (formats && formats.length > 0) {
                const detector = new window.BarcodeDetector({ formats });
                const results = await detector.detect(img);
                for (const res of results) {
                    if (res.rawValue) {
                        uniqueCodes.add(res.rawValue);
                    }
                }
            }
        } catch (e) {
            console.warn("Native BarcodeDetector failed, falling back to custom engine:", e);
        }
    }

    // Prepare formats & hints for ZXing MultiFormatReader
    const formats = [
        zx.BarcodeFormat.QR_CODE,
        zx.BarcodeFormat.CODE_128,
        zx.BarcodeFormat.CODE_39,
        zx.BarcodeFormat.CODE_93,
        zx.BarcodeFormat.CODABAR,
        zx.BarcodeFormat.DATA_MATRIX,
        zx.BarcodeFormat.ITF,
        zx.BarcodeFormat.EAN_13,
        zx.BarcodeFormat.EAN_8,
        zx.BarcodeFormat.PDF_417,
        zx.BarcodeFormat.UPC_A,
        zx.BarcodeFormat.UPC_E
    ];

    const hints = new Map();
    hints.set(zx.DecodeHintType.POSSIBLE_FORMATS, formats);
    hints.set(zx.DecodeHintType.TRY_HARDER, true);

    const reader = new zx.MultiFormatReader(false, hints);

    // Try both binarizers: HybridBinarizer (standard) and GlobalHistogramBinarizer (better for high-contrast scans)
    const decodeCanvasWithZXing = (canvas) => {
        const luminanceSource = new zx.HTMLCanvasElementLuminanceSource(canvas);
        const binarizers = [
            zx.HybridBinarizer,
            zx.GlobalHistogramBinarizer
        ];

        for (const Binarizer of binarizers) {
            try {
                const binaryBitmap = new zx.BinaryBitmap(new Binarizer(luminanceSource));
                const result = reader.decode(binaryBitmap);
                if (result && result.text) {
                    return result.text;
                }
            } catch (e) {
                // Ignore decoding failure for this combination
            }
        }
        return null;
    };

    // 2. Multi-scale & crop & rotation processing pipelines
    // These options alter scaling, aspect ratios, orientations, and filters to expose barcode lines.
    const pipelines = [];

    // Let's test standard rotations (0, 90, 180, 270, 5, -5) at different scales and crops
    const rotations = [0, 90, 180, 270, 5, -5];
    const scales = [null, 1200, 800, 600, 400];
    const crops = [null, 'top', 'bottom', 'center'];

    // Prioritize no-rotation standard scales first (common case)
    for (const scale of scales) {
        for (const crop of crops) {
            pipelines.push({ rotation: 0, maxDimension: scale, crop });
        }
    }

    // Then try rotated configurations (fixes camera angles, landscape snapshots, and minor tilts)
    for (const rot of rotations) {
        if (rot === 0) continue;
        for (const scale of [800, 600]) {
            for (const crop of [null, 'center']) {
                pipelines.push({ rotation: rot, maxDimension: scale, crop });
            }
        }
    }

    // Grayscale and contrast options
    for (const filter of ['grayscale', 'highContrast']) {
        pipelines.push({ rotation: 0, maxDimension: 800, [filter]: true });
    }

    for (const pipeline of pipelines) {
        // If we found at least 2 unique codes (usually start and end serial numbers), we can stop early.
        if (uniqueCodes.size >= 2) {
            break;
        }

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            let sx = 0, sy = 0, sw = img.width, sh = img.height;

            // Apply crop options
            if (pipeline.crop === 'top') {
                sh = Math.round(img.height * 0.6);
            } else if (pipeline.crop === 'bottom') {
                sy = Math.round(img.height * 0.4);
                sh = Math.round(img.height * 0.6);
            } else if (pipeline.crop === 'center') {
                sx = Math.round(img.width * 0.1);
                sy = Math.round(img.height * 0.1);
                sw = Math.round(img.width * 0.8);
                sh = Math.round(img.height * 0.8);
            }

            let dw = sw;
            let dh = sh;

            // Apply max dimension constraint
            if (pipeline.maxDimension) {
                const maxDim = pipeline.maxDimension;
                if (sw > maxDim || sh > maxDim) {
                    if (sw > sh) {
                        dh = Math.round((maxDim / sw) * sh);
                        dw = maxDim;
                    } else {
                        dw = Math.round((maxDim / sh) * sw);
                        dh = maxDim;
                    }
                }
            }

            // Adjust canvas size if sideways rotation
            const angleRad = (pipeline.rotation || 0) * Math.PI / 180;
            const isSideways = pipeline.rotation === 90 || pipeline.rotation === 270;

            canvas.width = isSideways ? dh : dw;
            canvas.height = isSideways ? dw : dh;

            // Translate, rotate, and draw
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(angleRad);
            ctx.drawImage(img, sx, sy, sw, sh, -dw / 2, -dh / 2, dw, dh);

            // Apply image filters (Grayscale or high contrast)
            if (pipeline.grayscale || pipeline.highContrast) {
                const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imgData.data;
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i+1];
                    const b = data[i+2];
                    
                    let gray = 0.299 * r + 0.587 * g + 0.114 * b;
                    if (pipeline.highContrast) {
                        // Maximize contrast: push colors to extremes
                        gray = gray < 128 ? Math.max(0, gray - 60) : Math.min(255, gray + 60);
                    }
                    
                    data[i] = gray;
                    data[i+1] = gray;
                    data[i+2] = gray;
                }
                ctx.putImageData(imgData, 0, 0);
            }

            const decodedText = decodeCanvasWithZXing(canvas);
            if (decodedText) {
                uniqueCodes.add(decodedText);
            }
        } catch (err) {
            // Log non-ZXing runtime issues (e.g. TypeErrors) to console
            if (err && err.name !== 'NotFoundException' && err.name !== 'ChecksumException' && err.name !== 'FormatException') {
                console.error("Pipeline runtime error during decode:", err);
            }
        }
    }

    return Array.from(uniqueCodes);
};

const ScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
    const [error, setError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [activeTab, setActiveTab] = useState('camera'); // 'camera' | 'upload'
    const [detectedCodes, setDetectedCodes] = useState([]);
    const [selectedCode, setSelectedCode] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [isProcessingFile, setIsProcessingFile] = useState(false);
    const fileInputRef = useRef(null);
    const scannerRef = useRef(null);
    const isMountedRef = useRef(true);

    const stopScanner = useCallback(async () => {
        if (scannerRef.current) {
            try {
                if (scannerRef.current.isScanning) {
                    await scannerRef.current.stop();
                }
                scannerRef.current.clear();
            } catch (err) {
                console.error("Error stopping scanner:", err);
            }
            scannerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        isMountedRef.current = true;

        if (activeTab !== 'camera') return;

        const startScanner = async () => {
            try {
                setError(null);
                setIsScanning(false);

                // Wait for DOM to be ready
                await new Promise((resolve) => setTimeout(resolve, 200));

                if (!isMountedRef.current) return;

                const element = document.getElementById("scanner-reader");
                if (!element) {
                    throw new Error("Scanner reader element not found.");
                }

                // Stop any existing scanner first
                await stopScanner();

                const html5QrCode = new Html5Qrcode("scanner-reader", {
                    verbose: false,
                });
                scannerRef.current = html5QrCode;

                const cameraConstraints = {
                    facingMode: "environment",
                    advanced: [
                        { focusMode: "continuous" },
                        { width: { min: 1280, ideal: 1920 } },
                        { height: { min: 720, ideal: 1080 } },
                    ]
                };

                await html5QrCode.start(
                    cameraConstraints,
                    {
                        fps: 15,
                        qrbox: (viewfinderWidth, viewfinderHeight) => {
                            const w = Math.floor(viewfinderWidth * 0.85);
                            const h = Math.floor(viewfinderHeight * 0.25);
                            return { width: Math.max(w, 200), height: Math.max(h, 80) };
                        },
                        aspectRatio: 1.0,
                        disableFlip: false,
                    },
                    (decodedText) => {
                        if (isMountedRef.current) {
                            onScanSuccess(decodedText);
                        }
                    },
                    () => {
                        // Ignore frame error noise
                    }
                );

                if (isMountedRef.current) {
                    setIsScanning(true);

                    // Try to apply continuous auto-focus and macro adjustments
                    try {
                        const videoElement = element.querySelector('video');
                        if (videoElement && videoElement.srcObject) {
                            const track = videoElement.srcObject.getVideoTracks()[0];
                            if (track) {
                                const capabilities = track.getCapabilities?.();
                                const settings = {};

                                if (capabilities?.focusMode?.includes('continuous')) {
                                    settings.focusMode = 'continuous';
                                }
                                if (capabilities?.focusDistance) {
                                    settings.focusDistance = capabilities.focusDistance.min;
                                }

                                if (Object.keys(settings).length > 0) {
                                    await track.applyConstraints({ advanced: [settings] });
                                }
                            }
                        }
                    } catch (focusErr) {
                        console.log("Autofocus optimization not supported:", focusErr.message);
                    }
                } else {
                    await html5QrCode.stop();
                }
            } catch (err) {
                console.error("Error starting camera scanner:", err);
                if (isMountedRef.current) {
                    let friendlyMessage = err?.message || "Failed to access the camera.";
                    const errStr = String(err).toLowerCase();
                    if (errStr.includes("permissiondenied") || errStr.includes("notallowed") || errStr.includes("permission")) {
                        friendlyMessage = "Camera access denied. Please enable camera permission in your browser settings to scan.";
                    } else if (errStr.includes("notfound") || errStr.includes("no video input")) {
                        friendlyMessage = "No camera device found on this system.";
                    }
                    setError(friendlyMessage);
                }
            }
        };

        startScanner();

        return () => {
            isMountedRef.current = false;
            stopScanner();
        };
    }, [isOpen, activeTab, onScanSuccess, stopScanner]);

    // Handle file upload scanning
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadError(null);
        setDetectedCodes([]);
        setSelectedCode(null);
        setIsProcessingFile(true);

        try {
            const img = await loadImage(file);
            const codes = await scanImageForBarcodes(img);

            if (isMountedRef.current) {
                if (codes.length === 0) {
                    setUploadError("Could not detect any barcode or QR code in this image. Please try a clearer image.");
                } else {
                    setDetectedCodes(codes);
                    setSelectedCode(codes[0]); // Default selection to the first code found
                }
            }
        } catch (err) {
            console.error("File scan error:", err);
            if (isMountedRef.current) {
                setUploadError("Could not parse image. Please ensure it is a valid format.");
            }
        } finally {
            if (isMountedRef.current) {
                setIsProcessingFile(false);
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleUseResult = () => {
        if (selectedCode) {
            onScanSuccess(selectedCode);
        }
    };

    const handleTabSwitch = async (tab) => {
        if (tab === activeTab) return;

        if (tab === 'upload') {
            await stopScanner();
            setIsScanning(false);
        }

        setActiveTab(tab);
        setError(null);
        setUploadError(null);
        setDetectedCodes([]);
        setSelectedCode(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl flex flex-col relative">
                {/* Header */}
                <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-950">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        Barcode / QR Scanner
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors text-2xl font-semibold leading-none focus:outline-none"
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>

                {/* Tab Switcher */}
                <div className="flex border-b border-gray-800 bg-gray-950">
                    <button
                        onClick={() => handleTabSwitch('camera')}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                            activeTab === 'camera'
                                ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-900/50'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Camera
                    </button>
                    <button
                        onClick={() => handleTabSwitch('upload')}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                            activeTab === 'upload'
                                ? 'text-blue-400 border-b-2 border-blue-500 bg-gray-900/50'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Upload Image
                    </button>
                </div>

                {/* Body */}
                <div className="relative p-4 flex-1 flex flex-col items-center justify-center min-h-[320px] bg-gray-950">

                    {/* === CAMERA TAB === */}
                    {activeTab === 'camera' && (
                        <>
                            {error ? (
                                <div className="w-full p-5 bg-red-950/40 border border-red-900/60 rounded-2xl text-red-200 text-sm flex flex-col items-center text-center gap-4 animate-fadeIn">
                                    <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center border border-red-700/50 shadow-inner">
                                        <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="font-semibold text-red-400">Scanner Setup Failed</p>
                                        <p className="text-xs text-red-300/70 leading-relaxed px-2">{error}</p>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="mt-2 px-5 py-2 bg-red-900/50 hover:bg-red-800 border border-red-700/80 rounded-xl text-xs font-semibold text-white transition-all shadow-sm active:scale-95"
                                    >
                                        Close Modal
                                    </button>
                                </div>
                            ) : (
                                <div className="relative w-full max-w-sm mx-auto aspect-square bg-black rounded-2xl overflow-hidden border border-gray-800 shadow-inner flex items-center justify-center scanner-container">
                                    {/* Camera video goes here */}
                                    <div id="scanner-reader" className="w-full h-full"></div>

                                    {/* Clean Custom Viewfinder */}
                                    {isScanning && (
                                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                            <div className="relative flex items-center justify-center" style={{ width: '85%', height: '25%' }}>
                                                {/* Corner brackets */}
                                                <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-blue-400 rounded-tl-md"></div>
                                                <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-blue-400 rounded-tr-md"></div>
                                                <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-blue-400 rounded-bl-md"></div>
                                                <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-blue-400 rounded-br-md"></div>

                                                {/* Scanning laser line */}
                                                <div className="animate-scan"></div>
                                            </div>
                                        </div>
                                    )}

                                    {!isScanning && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-950">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                            <p className="text-gray-400 text-xs font-medium animate-pulse">Starting camera stream...</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isScanning && (
                                <p className="text-gray-500 text-xs mt-3 text-center">
                                    Hold the barcode within the frame to scan
                                </p>
                            )}
                        </>
                    )}

                    {/* === UPLOAD TAB === */}
                    {activeTab === 'upload' && (
                        <div className="w-full flex flex-col items-center gap-4">
                            {/* Upload Area */}
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full max-w-sm border-2 border-dashed border-gray-700 hover:border-blue-500 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors bg-gray-900/50 hover:bg-gray-800/50 min-h-[200px]"
                            >
                                {isProcessingFile ? (
                                    <>
                                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                                        <p className="text-gray-300 text-sm font-medium">Processing & searching barcodes...</p>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-14 h-14 bg-gray-800 rounded-2xl flex items-center justify-center border border-gray-700">
                                            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                            </svg>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-gray-200 text-sm font-semibold">Tap to upload an image</p>
                                            <p className="text-gray-500 text-xs mt-1">JPG, PNG, or WEBP</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileUpload}
                                className="hidden"
                            />

                            {/* Upload Error */}
                            {uploadError && (
                                <div className="w-full max-w-sm p-3 bg-red-950/40 border border-red-900/60 rounded-xl text-red-300 text-xs text-center leading-relaxed">
                                    {uploadError}
                                </div>
                            )}

                            {/* Upload Results & Option Selector */}
                            {detectedCodes.length > 0 && (
                                <div className="w-full max-w-sm p-4 bg-green-950/40 border border-green-800/60 rounded-xl flex flex-col items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-green-300 text-sm font-semibold">
                                            {detectedCodes.length === 1 ? "Barcode Detected" : `Detected ${detectedCodes.length} Barcodes`}
                                        </span>
                                    </div>

                                    {detectedCodes.length === 1 ? (
                                        <p className="text-white text-sm font-mono bg-gray-800 px-4 py-2.5 rounded-lg w-full text-center break-all border border-gray-700">
                                            {detectedCodes[0]}
                                        </p>
                                    ) : (
                                        <div className="w-full flex flex-col gap-2">
                                            <p className="text-gray-400 text-xs text-center mb-1">
                                                Select the code you want to use:
                                            </p>
                                            {detectedCodes.map((code) => (
                                                <button
                                                    key={code}
                                                    onClick={() => setSelectedCode(code)}
                                                    className={`w-full px-3 py-2 text-sm font-mono text-center break-all border rounded-lg transition-all ${
                                                        selectedCode === code
                                                            ? 'bg-blue-600/35 border-blue-500 text-blue-300 font-semibold'
                                                            : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700/60'
                                                    }`}
                                                >
                                                    {code}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <button
                                        onClick={handleUseResult}
                                        disabled={!selectedCode}
                                        className="w-full mt-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-800 disabled:text-gray-500 disabled:border-transparent disabled:cursor-not-allowed text-white rounded-xl text-sm font-semibold transition-all active:scale-95 shadow-md"
                                    >
                                        Use Selected Code
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-800 flex justify-center bg-gray-950">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700 rounded-xl text-sm font-semibold transition-all hover:scale-105 active:scale-95 shadow-md"
                    >
                        Cancel / Close Camera
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScannerModal;
